<template>
  <DetailsContentPane>
    <DetailsSection label="Route">
      <OverviewRoutePanel />
    </DetailsSection>

    <DetailsSection 
      label="Kabine"
      class="border-top-0"
      :has-icon="true"
    >
      <template #icon>
        <Icon 
          type="edit"
          @click="setDetailsPanelActiveTab('cabin')"
        />
      </template>

      <CabinPanel />
    </DetailsSection>

    <template v-if="transportation === 'Flug'">
      <DetailsSection 
        label="Flug"
        :type="isSelectableRailOption ? '' : 'last'"
        :has-icon="true"
        class="border-top-0"
      >
        <template #icon>
          <Icon 
            type="edit"
            @click="setDetailsPanelActiveTab('transportation')"
          />
        </template>

        <div class="container">
          <div class="row">
            <div class="col">
              <FlightPanel
                :flight="selectedAidaFlight?.outboundFlight"
                :message="flightMessage('outbound')"
                direction="O"
              />
            </div>
            <div class="col">
              <FlightPanel
                :flight="selectedAidaFlight?.inboundFlight"
                :message="flightMessage('inbound')"
                direction="I"
              />
            </div>
          </div>
        </div>
      </DetailsSection>

      <DetailsSection 
        v-if="isSelectableRailOption"
        label="An-/Abreise Bahn" 
        type="last"
        :has-icon="true"
        class="border-top-0"
      >
        <template #icon>
          <Icon 
            type="edit"
            @click="setDetailsPanelActiveTab('transportation')"
          />
        </template>

        <RailPanel
          :rail="railOptionRNF" 
          :message="setRailMessage(selectableRailOptionsRNF, railDirectionRNF)" 
          :direction="railDirectionRNF"
        />
      </DetailsSection>
    </template>

    <template v-if="transportation === 'Wunschflug'">
      <DetailsSection 
        label="Wunschflug"
        :type="!!railOptionFlexRNF ? '' : 'last'"
        :has-icon="true"
        class="border-top-0"
      >
        <template #icon>
          <Icon 
            type="edit"
            @click="setDetailsPanelActiveTab('transportation')"
          />
        </template>

        <div class="container mb-3">
          <div class="row">
            <div class="col">
              <FlightPanel
                :flight="selectedFlexFlight?.outboundFlight"
                :message="selectedFlexFlight?.outboundFlight ? '' : 'Kein Hinflug ausgewählt'"
                direction="O"
              />
            </div>
            <div class="col">
              <FlightPanel
                :flight="selectedFlexFlight?.inboundFlight"
                :message="selectedFlexFlight?.inboundFlight ? '' : 'Kein Rückflug ausgewählt'"
                direction="I"
              />
            </div>
          </div>
        </div>
      </DetailsSection>

      <DetailsSection 
        v-if="isSelectableFlexRailOption"
        label="An-/Abreise Bahn" 
        type="last"
        :has-icon="true"
        class="border-top-0"
      >
        <template #icon>
          <Icon 
            type="edit"
            @click="setDetailsPanelActiveTab('transportation')"
          />
        </template>

        <RailPanel 
          :rail="railOptionFlexRNF" 
          :message="railErrorMessage" 
          :direction="railForFlexFlightDirection"
        />
      </DetailsSection>
    </template>

    <DetailsSection 
      v-if="transportation === 'Bahn'"
      label="An-/Abreise Bahn" 
      type="last"
      :has-icon="true"
      class="border-top-0"
    >
      <template #icon>
        <Icon 
          type="edit"
          @click="setDetailsPanelActiveTab('transportation')"
        />
      </template>

      <RailPanel 
        :rail="railOption" 
        :message="setRailMessage(selectableRITRailOptions, railDirection)" 
        :direction="railDirection"
      />
    </DetailsSection>

    <template v-if="transportation === 'Individuell'">
      <DetailsSection 
        label="An-/Abreise Individuell" 
        type="last"
        :has-icon="true"
        class="border-top-0"
      >
        <template #icon>
          <Icon 
            type="edit"
            @click="setDetailsPanelActiveTab('transportation')"
          />
        </template>

        <IndividualPanel />
      </DetailsSection>
    </template>
  </DetailsContentPane>
</template>

<script lang="ts">
import Icon from '@/components/common/Icon.vue'
import { useCruiseStore, useDetailsStore, useFlexFlightStore, useFlightStore, useRailStore, useUiStore } from '@/store'
import { flightMessage } from '@/store/details/flight/actions'
import { mapRailErrorText } from '@/store/details/rail/helper'
import { IError, IFetchRailOptionByDirection, RailDirection } from '@/store/details/rail/interfaces'
import { mapActions, mapState } from 'pinia'
import { defineComponent } from 'vue'
import DetailsContentPane from '../DetailsContentPane.vue'
import DetailsSection from '../DetailsSection.vue'
import FlightPanel from '../transportation/FlightPanel.vue'
import IndividualPanel from '../transportation/IndividualPanel.vue'
import RailPanel from '../transportation/RailPanel.vue'
import CabinPanel from './CabinPanel.vue'
import OverviewRoutePanel from './OverviewRoutePanel.vue'

export default defineComponent({
  components: {
    DetailsContentPane,
    DetailsSection,
    OverviewRoutePanel,
    CabinPanel,
    RailPanel,
    IndividualPanel,
    Icon,
    FlightPanel
  },

  computed: {
    ...mapState(useFlexFlightStore, {
      railErrorMessage: store => store.railErrorMessage,
      selectedFlexFlight: store => store.selectedFlight,
      railForFlexFlightDirection: store => store.railDirection,
      railOptionFlexRNF: store => store.selectedRailOption,
    }),
    ...mapState(useDetailsStore, {
      transportation: store => store.transportation,
    }),
    ...mapState(useCruiseStore, {
      route: store => store.route
    }),
    ...mapState(useFlightStore, {
      railDirectionRNF: store => store.railDirectionRNF,
      railOptionRNF: store => store.selectedRailOption,
      selectedAidaFlight: store => store.selectedFlight,
      selectableRailOptionsRNF: store => store.selectableRailOptions
    }),
    ...mapState(useRailStore, {
      railOption: store => store.selectedRailOption,
      railDirection: store => store.railDirection,
      selectableRITRailOptions: store => store.selectableRailOptions
    }),

    isSelectableRailOption() {
      return !!this.railOptionRNF && 'railClassCode' in this.railOptionRNF && !!this.railOptionRNF.railClassCode
    },

    isSelectableFlexRailOption() {
      return !!this.railOptionFlexRNF && 'railClassCode' in this.railOptionFlexRNF && !!this.railOptionFlexRNF.railClassCode
    }
  },

  methods: {
    ...mapActions(useFlightStore, {
      flightMessage: 'flightMessage'
    }),
    ...mapActions(useUiStore, {
      setDetailsPanelActiveTab: 'setDetailsPanelActiveTab'
    }),

    setRailMessage(availableRailOptions: IFetchRailOptionByDirection | IError, selectedRailDirection: RailDirection) {
      return mapRailErrorText(this.route, availableRailOptions, selectedRailDirection)
    },
  },
})
</script>